import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { billingAPI, settingsAPI } from '../services/api';

const PLANS = [
    {
        id: 'starter',
        name: 'Starter',
        subtitle: 'Perfect for solo agents',
        monthlyPrice: 49,
        annualPrice: 39,
        icon: 'rocket_launch',
        features: [
            { text: 'Up to 100 leads/month', included: true },
            { text: 'Gmail OR Microsoft Outlook', included: true },
            { text: 'Email + SMS + WhatsApp(API)', included: true },
            { text: '5 response templates', included: true },
            { text: '10 email parsers', included: true },
            { text: 'Lead dashboard', included: true },
            { text: 'Lead scoring (Hot/Warm/Cold)', included: true },
            { text: 'Response analytics', included: true },
            { text: 'Email support', included: true },
        ],
    },
    {
        id: 'professional',
        name: 'Professional',
        subtitle: 'For serious agents',
        monthlyPrice: 89,
        annualPrice: 71,
        popular: true,
        icon: 'diamond',
        features: [
            { text: 'Up to 500 leads/month', included: true },
            { text: 'Gmail AND Microsoft Outlook', included: true },
            { text: 'Email + SMS + WhatsApp(API)', included: true },
            { text: 'Unlimited templates', included: true },
            { text: '30+ platform parsers (global)', included: true },
            { text: 'Lead scoring (Hot/Warm/Cold)', included: true },
            { text: 'Lead Notes/Comments', included: true },
            { text: 'Response analytics', included: true },
            { text: 'Priority support', included: true },
            { text: 'Everything in Starter', included: true },
        ],
    },
    {
        id: 'team',
        name: 'Team',
        subtitle: 'For brokerages & teams',
        monthlyPrice: 199,
        annualPrice: 159,
        icon: 'groups',
        features: [
            { text: 'Unlimited leads', included: true },
            { text: 'Gmail AND Microsoft for 5 users', included: true },
            { text: 'All channels + priorities', included: true },
            { text: 'Unlimited templates', included: true },
            { text: 'All 30+ parsers', included: true },
            { text: 'Dedicated support', included: true },
            { text: 'Everything in Professional', included: true },
        ],
    },
];

const Billing = () => {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [billingInterval, setBillingInterval] = useState('annual');
    const [checkoutLoading, setCheckoutLoading] = useState(null); // plan id
    const [cancelLoading, setCancelLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [successBanner, setSuccessBanner] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [subRes, usageRes] = await Promise.all([
                billingAPI.getSubscription(),
                settingsAPI.getUsage().catch(() => null),
            ]);
            setSubscription(subRes.data);
            if (usageRes) setUsage(usageRes.data.usage || usageRes.data);
        } catch (error) {
            console.error('Error fetching billing data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle success redirect from Dodo checkout
    useEffect(() => {
        if (searchParams.get('success') === 'true') {
            setSuccessBanner(true);
            fetchData();
            const timer = setTimeout(() => setSuccessBanner(false), 8000);
            return () => clearTimeout(timer);
        }
    }, [searchParams, fetchData]);

    const handleCheckout = async (planId) => {
        try {
            setCheckoutLoading(planId);
            const res = await billingAPI.createCheckout(planId, billingInterval);
            if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Failed to create checkout session. Please try again.');
        } finally {
            setCheckoutLoading(null);
        }
    };

    const handleCancelSubscription = async () => {
        try {
            setCancelLoading(true);
            await billingAPI.cancelSubscription();
            setShowCancelModal(false);
            await fetchData();
        } catch (error) {
            console.error('Cancel error:', error);
            alert('Failed to cancel subscription. Please try again.');
        } finally {
            setCancelLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={`min-h-screen p-8 flex items-center justify-center ${isDark ? 'bg-navy' : 'bg-mint'}`}>
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-cyan' : 'border-primary'}`}></div>
            </div>
        );
    }

    // Derived state
    const currentPlan = subscription?.plan || 'free';
    const subStatus = subscription?.status || 'inactive';
    const isTrial = subStatus === 'trialing';
    const isActive = subStatus === 'active';
    const trialDaysRemaining = subscription?.trial_days_remaining;
    const leadsUsed = subscription?.leads_used_this_month ?? 0;
    const leadsLimit = subscription?.leads_limit ?? 0;
    const leadsPercent = leadsLimit > 0 ? Math.min(Math.round((leadsUsed / leadsLimit) * 100), 100) : 0;

    const smsUsed = subscription?.sms_used ?? 0;
    const smsLimit = subscription?.sms_limit ?? 0;
    const smsPercent = smsLimit > 0 ? Math.min(Math.round((smsUsed / smsLimit) * 100), 100) : 0;
    const emailsSent = subscription?.emails_sent ?? 0;

    const planName = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);
    const currentPlanObj = PLANS.find(p => p.id === currentPlan);
    const actualInterval = subscription?.interval || 'monthly';
    const currentPrice = currentPlanObj
        ? (actualInterval === 'annual' ? currentPlanObj.annualPrice : currentPlanObj.monthlyPrice)
        : 0;

    const statusColor = {
        active: 'bg-green-500',
        trialing: 'bg-yellow-500',
        cancelled: 'bg-red-500',
        expired: 'bg-red-500',
        past_due: 'bg-orange-500',
        inactive: 'bg-gray-500',
    }[subStatus] || 'bg-gray-500';

    const statusLabel = {
        active: 'Active',
        trialing: 'Free Trial',
        cancelled: 'Cancelled',
        expired: 'Expired',
        past_due: 'Past Due',
        inactive: 'Inactive',
    }[subStatus] || 'Inactive';

    const getPlanAction = (plan) => {
        if (plan.id === currentPlan && (isActive || isTrial)) return 'current';
        const planOrder = ['free', 'starter', 'professional', 'team'];
        const currentIndex = planOrder.indexOf(currentPlan);
        const planIndex = planOrder.indexOf(plan.id);
        if (planIndex > currentIndex) return 'upgrade';
        return 'downgrade';
    };

    return (
        <div className={`min-h-screen ${isDark ? 'bg-navy text-light' : 'bg-mint text-darkTeal'} pb-12 relative`}>

            {/* Success Banner */}
            {successBanner && (
                <div className="mb-6 p-4 rounded-xl bg-green-500/20 border border-green-500/30 flex items-center gap-3 animate-fade-in">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                        <span className="material-symbols-outlined text-green-500">check_circle</span>
                    </div>
                    <div>
                        <p className="font-semibold text-green-500">Payment Successful!</p>
                        <p className={`text-sm ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>Your subscription has been activated. Thank you!</p>
                    </div>
                    <button onClick={() => setSuccessBanner(false)} className="ml-auto text-green-500 hover:text-green-400">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            )}

            {/* Trial Warning Banner */}
            {isTrial && trialDaysRemaining !== null && trialDaysRemaining <= 7 && (
                <div className={`mb-6 p-4 rounded-xl border flex items-center gap-3 ${trialDaysRemaining <= 3
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-yellow-500/10 border-yellow-500/30'
                    }`}>
                    <span className={`material-symbols-outlined ${trialDaysRemaining <= 3 ? 'text-red-500' : 'text-yellow-500'}`}>warning</span>
                    <div>
                        <p className={`font-semibold ${trialDaysRemaining <= 3 ? 'text-red-500' : 'text-yellow-500'}`}>
                            {trialDaysRemaining === 0 ? 'Trial expires today!' : `${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''} left in trial`}
                        </p>
                        <p className={`text-sm ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>
                            Choose a plan below to keep access to all features.
                        </p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-6 gap-4">
                <div>
                    <h2 className={`text-2xl font-bold mb-1 text-left ${isDark ? 'text-light' : 'text-darkTeal'}`}>Billing</h2>
                    <p className={`text-sm text-left ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>Manage your subscription and payment</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate('/help')}
                        className={`px-4 py-2 border rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${isDark
                            ? 'bg-navy border-light/20 text-light/80 hover:text-pink hover:border-pink'
                            : 'bg-white border-teal/20 text-darkTeal/80 hover:text-pink hover:border-pink'
                            }`}>
                        <span className="material-symbols-outlined text-base">help</span>
                        Support
                    </button>
                </div>
            </div>

            {/* Current Plan Card */}
            <div className={`rounded-xl border p-8 mb-8 relative overflow-hidden shadow-lg ${isDark
                ? 'border-cyan/30 bg-gradient-to-br from-cyan/20 to-cyan/5 shadow-[0_4px_30px_rgba(8,217,214,0.1)]'
                : 'border-primary/30 bg-gradient-to-br from-primary/20 to-primary/5 shadow-lg'
                }`}>
                <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none ${isDark ? 'bg-cyan/10' : 'bg-primary/10'}`}></div>

                <div className="flex flex-col md:flex-row justify-between items-start relative z-10">
                    <div className="text-left">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-wider border ${isDark
                                ? 'bg-cyan/20 text-cyan border-cyan/20'
                                : 'bg-primary/20 text-darkTeal border-primary/20'
                                }`}>Current Plan</span>
                            <span className={`text-xs font-bold px-2 py-1 rounded uppercase tracking-wider border ${statusColor}/20 text-${statusColor.replace('bg-', '')} border-${statusColor.replace('bg-', '')}/20`}
                                style={{
                                    backgroundColor: `${statusColor === 'bg-green-500' ? 'rgba(34,197,94,0.2)' : statusColor === 'bg-yellow-500' ? 'rgba(234,179,8,0.2)' : statusColor === 'bg-red-500' ? 'rgba(239,68,68,0.2)' : 'rgba(156,163,175,0.2)'}`,
                                    color: statusColor === 'bg-green-500' ? '#22c55e' : statusColor === 'bg-yellow-500' ? '#eab308' : statusColor === 'bg-red-500' ? '#ef4444' : '#9ca3af',
                                    borderColor: statusColor === 'bg-green-500' ? 'rgba(34,197,94,0.3)' : statusColor === 'bg-yellow-500' ? 'rgba(234,179,8,0.3)' : statusColor === 'bg-red-500' ? 'rgba(239,68,68,0.3)' : 'rgba(156,163,175,0.3)',
                                }}>
                                {statusLabel}
                            </span>
                            <span className={`text-sm flex items-center gap-1 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>
                                <span className={`w-2 h-2 rounded-full ${statusColor}`}></span>
                                {isActive ? 'Active' : isTrial ? 'Trial' : subStatus}
                            </span>
                        </div>
                        <h3 className={`text-4xl font-bold mb-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>{planName}</h3>
                        <p className={`text-lg ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>
                            {isTrial ? (
                                <>
                                    {trialDaysRemaining !== null && (
                                        <span className={isDark ? 'text-yellow-400' : 'text-yellow-600'}>
                                            {trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''} remaining
                                        </span>
                                    )}
                                    <br />
                                    <span className="text-sm">Upgrade to a paid plan to continue after your trial.</span>
                                </>
                            ) : isActive && currentPlanObj ? (
                                <>
                                    <span className={`font-bold text-2xl ${isDark ? 'text-cyan' : 'text-primary'}`}>${currentPrice}</span> / {actualInterval === 'annual' ? 'mo (billed annually)' : 'month'}
                                </>
                            ) : (
                                <span className="text-sm">No active subscription. Choose a plan below.</span>
                            )}
                        </p>
                    </div>

                </div>
            </div>

            {/* Usage + Payment Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                {/* Usage Overview */}
                <div className={`rounded-xl border p-6 shadow-sm ${isDark ? 'bg-navy border-light/10' : 'bg-white border-teal/10'}`}>
                    <h3 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                        <span className={`material-symbols-outlined ${isDark ? 'text-cyan' : 'text-primary'}`}>bar_chart</span>
                        Usage This Month
                    </h3>
                    <div className="space-y-6">
                        {/* Leads Progress */}
                        <div>
                            <div className="flex justify-between text-sm mb-2">
                                <span className={`font-medium ${isDark ? 'text-light/80' : 'text-darkTeal/80'}`}>Leads Captured</span>
                                <span className={`font-bold ${isDark ? 'text-cyan' : 'text-primary'}`}>
                                    {leadsUsed} <span className={`font-normal ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>/ {leadsLimit === 999999 ? '∞' : leadsLimit}</span>
                                </span>
                            </div>
                            <div className={`w-full rounded-full h-2.5 overflow-hidden border ${isDark ? 'bg-navy border-light/10' : 'bg-gray-100 border-gray-200'}`}>
                                <div
                                    className={`h-2.5 rounded-full transition-all duration-500 ${leadsPercent > 90 ? 'bg-red-500' : (isDark ? 'bg-cyan' : 'bg-primary')}`}
                                    style={{ width: `${leadsPercent}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* SMS Progress */}
                        <div>
                            <div className="flex justify-between text-sm mb-2">
                                <span className={`font-medium ${isDark ? 'text-light/80' : 'text-darkTeal/80'}`}>SMS Sent</span>
                                <span className={`font-bold ${isDark ? 'text-cyan' : 'text-primary'}`}>
                                    {smsUsed} <span className={`font-normal ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>/ {smsLimit}</span>
                                </span>
                            </div>
                            <div className={`w-full rounded-full h-2.5 overflow-hidden border ${isDark ? 'bg-navy border-light/10' : 'bg-gray-100 border-gray-200'}`}>
                                <div
                                    className={`h-2.5 rounded-full transition-all duration-500 ${smsPercent > 90 ? 'bg-red-500' : 'bg-gradient-to-r from-cyan to-blue-500'}`}
                                    style={{ width: `${smsPercent}%` }}
                                ></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <div className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'bg-light/5 border-light/5' : 'bg-teal/5 border-teal/5'}`}>
                                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center text-green-500">
                                    <span className="material-symbols-outlined text-sm">chat</span>
                                </div>
                                <div>
                                    <p className={`text-sm text-left font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>WhatsApp</p>
                                    <p className={`text-xs text-left ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>Included in SMS</p>
                                </div>
                            </div>
                            <div className={`flex items-center gap-3 p-3 rounded-lg border ${isDark ? 'bg-light/5 border-light/5' : 'bg-teal/5 border-teal/5'}`}>
                                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-500">
                                    <span className="material-symbols-outlined text-sm">email</span>
                                </div>
                                <div>
                                    <p className={`text-sm text-left font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Email</p>
                                    <p className={`text-xs text-left ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>{emailsSent} sent</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Stats / Subscription Info */}
                <div className={`rounded-xl border p-6 shadow-sm flex flex-col ${isDark ? 'bg-navy border-light/10' : 'bg-white border-teal/10'}`}>
                    <h3 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-light' : 'text-darkTeal'}`}>
                        <span className={`material-symbols-outlined ${isDark ? 'text-cyan' : 'text-primary'}`}>credit_card</span>
                        Subscription Details
                    </h3>
                    <div className="flex-1 space-y-4">
                        {[
                            { label: 'Plan', value: planName },
                            { label: 'Status', value: statusLabel },
                            { label: 'Billing', value: subscription?.interval ? subscription.interval.charAt(0).toUpperCase() + subscription.interval.slice(1) : '—' },
                            { label: 'Lead Limit', value: leadsLimit === 999999 ? 'Unlimited' : `${leadsLimit} / month` },
                        ].map((item, i) => (
                            <div key={i} className={`flex justify-between items-center py-3 border-b ${isDark ? 'border-light/5' : 'border-teal/5'}`}>
                                <span className={`text-sm ${isDark ? 'text-light/60' : 'text-darkTeal/60'}`}>{item.label}</span>
                                <span className={`text-sm font-semibold ${isDark ? 'text-light' : 'text-darkTeal'}`}>{item.value}</span>
                            </div>
                        ))}
                    </div>

                    {isActive ? (
                        <div className="mt-6">
                            <div className={`p-4 rounded-lg text-center ${isDark ? 'bg-light/5' : 'bg-teal/5'}`}>
                                <p className={`text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                                    Your subscription is active. Enjoy all {planName} features.
                                </p>
                            </div>
                            {/* Subtle cancel link — intentionally understated */}
                            <button
                                onClick={() => setShowCancelModal(true)}
                                className={`mt-3 w-full text-center text-xs py-1 transition-colors ${isDark ? 'text-light/25 hover:text-red-400' : 'text-darkTeal/25 hover:text-red-500'}`}
                            >
                                Cancel subscription
                            </button>
                        </div>
                    ) : (
                        <div className={`mt-6 p-4 rounded-lg text-center ${isDark ? 'bg-light/5' : 'bg-teal/5'}`}>
                            <p className={`text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>
                                {isTrial ? 'Choose a plan below to activate your subscription.' : 'No active subscription.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Available Plans */}
            <div className="mb-12">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                    <h3 className={`text-xl font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Available Plans</h3>

                    {/* Monthly / Annual Toggle */}
                    <div className={`flex items-center gap-3 p-1 rounded-full border ${isDark ? 'border-light/10 bg-light/5' : 'border-teal/10 bg-teal/5'}`}>
                        <button
                            onClick={() => setBillingInterval('monthly')}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${billingInterval === 'monthly'
                                ? (isDark ? 'bg-cyan text-navy shadow-lg' : 'bg-primary text-white shadow-lg')
                                : (isDark ? 'text-light/60 hover:text-light' : 'text-darkTeal/60 hover:text-darkTeal')
                                }`}>
                            Monthly
                        </button>
                        <button
                            onClick={() => setBillingInterval('annual')}
                            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${billingInterval === 'annual'
                                ? (isDark ? 'bg-cyan text-navy shadow-lg' : 'bg-primary text-white shadow-lg')
                                : (isDark ? 'text-light/60 hover:text-light' : 'text-darkTeal/60 hover:text-darkTeal')
                                }`}>
                            Annual
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${billingInterval === 'annual'
                                ? 'bg-white/20 text-navy'
                                : (isDark ? 'bg-cyan/20 text-cyan' : 'bg-primary/20 text-primary')
                                }`}>-20%</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {PLANS.map((plan) => {
                        const action = getPlanAction(plan);
                        const price = billingInterval === 'annual' ? plan.annualPrice : plan.monthlyPrice;
                        const isLoading = checkoutLoading === plan.id;

                        return (
                            <div key={plan.id} className={`relative p-6 rounded-xl border transition-all flex flex-col ${plan.popular
                                ? (isDark
                                    ? 'bg-navy border-2 border-cyan shadow-lg shadow-[0_0_20px_rgba(8,217,214,0.15)] transform md:-translate-y-2'
                                    : 'bg-white border-2 border-primary shadow-xl transform md:-translate-y-2')
                                : (isDark
                                    ? 'bg-navy border-light/10 hover:border-light/30'
                                    : 'bg-white border-teal/10 hover:border-teal/30 shadow-sm')
                                }`}>

                                {plan.popular && (
                                    <div className={`absolute top-0 right-0 text-xs font-bold px-3 py-1 rounded-bl-lg rounded-tr-sm uppercase tracking-wider ${isDark ? 'bg-cyan text-navy' : 'bg-primary text-white'}`}>
                                        Most Popular
                                    </div>
                                )}

                                <div className="mb-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`material-symbols-outlined ${plan.popular ? (isDark ? 'text-cyan' : 'text-primary') : (isDark ? 'text-light/60' : 'text-darkTeal/60')}`}>
                                            {plan.icon}
                                        </span>
                                        <h4 className={`text-lg font-semibold ${plan.popular ? (isDark ? 'text-cyan' : 'text-primary') : (isDark ? 'text-light' : 'text-darkTeal')}`}>
                                            {plan.name}
                                        </h4>
                                    </div>
                                    {plan.subtitle && (
                                        <p className={`text-xs pl-8 ${isDark ? 'text-light/40' : 'text-darkTeal/40'}`}>{plan.subtitle}</p>
                                    )}
                                </div>

                                <div className="mb-5">
                                    <span className={`text-3xl font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>${price}</span>
                                    <span className={`text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>/mo</span>
                                    {billingInterval === 'annual' && (
                                        <span className={`ml-2 text-xs line-through ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>${plan.monthlyPrice}</span>
                                    )}
                                    {billingInterval === 'annual' && (
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-light/30' : 'text-darkTeal/30'}`}>Billed annually</span>
                                            <span className="text-[10px] font-bold text-pink bg-pink/10 px-1.5 py-0.5 rounded border border-pink/20">Save 20%</span>
                                        </div>
                                    )}
                                </div>

                                <ul className={`space-y-2.5 mb-8 flex-1 text-sm ${plan.popular ? (isDark ? 'text-light' : 'text-darkTeal') : (isDark ? 'text-light/70' : 'text-darkTeal/70')}`}>
                                    {plan.features.map((feat, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className={`material-symbols-outlined text-lg shrink-0 ${feat.included ? (isDark ? 'text-cyan' : 'text-primary') : (isDark ? 'text-light/20' : 'text-darkTeal/20')}`}>
                                                {feat.included ? 'check_circle' : 'cancel'}
                                            </span>
                                            <span className={!feat.included ? 'opacity-50' : ''}>{feat.text}</span>
                                        </li>
                                    ))}
                                </ul>

                                {action === 'current' ? (
                                    <button className={`w-full py-2.5 rounded-lg font-bold cursor-default opacity-90 ${isDark
                                        ? 'bg-cyan text-navy shadow-cyan/20 shadow-lg'
                                        : 'bg-primary text-white shadow-primary/20 shadow-lg'
                                        }`}>
                                        Current Plan
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleCheckout(plan.id)}
                                        disabled={isLoading}
                                        className={`w-full py-2.5 rounded-lg font-semibold border transition-all flex items-center justify-center gap-2 ${isLoading ? 'opacity-50 cursor-wait' : ''
                                            } ${action === 'upgrade'
                                                ? (isDark
                                                    ? 'bg-cyan/10 text-cyan border-cyan/30 hover:bg-cyan/20'
                                                    : 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20')
                                                : (isDark
                                                    ? 'bg-light/5 text-light/80 border-light/10 hover:bg-light/10'
                                                    : 'bg-teal/5 text-darkTeal/80 border-teal/10 hover:bg-teal/10')
                                            }`}>
                                        {isLoading ? (
                                            <>
                                                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                                Processing...
                                            </>
                                        ) : (
                                            <>
                                                <span className="material-symbols-outlined text-base">
                                                    {action === 'upgrade' ? 'upgrade' : 'arrow_downward'}
                                                </span>
                                                {action === 'upgrade' ? 'Upgrade' : 'Downgrade'}
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className={`mt-8 mb-4 flex items-center justify-between text-xs border-t pt-6 ${isDark ? 'text-light/30 border-light/5' : 'text-darkTeal/30 border-teal/5'}`}>
                <p>© 2026 OffTrigger.</p>
                <div className="flex gap-4">
                    <a className="hover:text-primary transition-colors" href="/privacy">Privacy</a>
                    <a className="hover:text-primary transition-colors" href="/terms">Terms</a>
                </div>
            </div>

            {/* Cancel Confirmation Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className={`w-full max-w-md rounded-2xl p-6 shadow-2xl border ${isDark ? 'bg-navy border-light/10' : 'bg-white border-teal/10'}`}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                                <span className="material-symbols-outlined text-red-500 text-2xl">warning</span>
                            </div>
                            <div>
                                <h3 className={`text-lg font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Cancel Subscription?</h3>
                                <p className={`text-sm ${isDark ? 'text-light/50' : 'text-darkTeal/50'}`}>This action can't be undone easily</p>
                            </div>
                        </div>
                        <p className={`text-sm mb-6 ${isDark ? 'text-light/70' : 'text-darkTeal/70'}`}>
                            If you cancel, you'll lose access to {planName} features at the end of your current billing period. Your data will be preserved but new lead capture will be paused.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelModal(false)}
                                className={`flex-1 py-2.5 rounded-lg font-medium border transition-colors ${isDark
                                    ? 'border-light/10 text-light hover:bg-light/5'
                                    : 'border-teal/10 text-darkTeal hover:bg-teal/5'
                                    }`}>
                                Keep Subscription
                            </button>
                            <button
                                onClick={handleCancelSubscription}
                                disabled={cancelLoading}
                                className="flex-1 py-2.5 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center gap-2">
                                {cancelLoading ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Cancelling...
                                    </>
                                ) : (
                                    'Yes, Cancel'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Billing;
